import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { FlaskConical, X, Users, Mic2, Sparkles, ChevronDown, RotateCw } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';

const PERSONA_META = {
  user: { label: 'Buyer', icon: Users, route: '/dashboard' },
  artist: { label: 'Artist', icon: Mic2, route: '/artist-dashboard' },
  agent: { label: 'Agent', icon: Sparkles, route: '/agent-dashboard' },
};

export function TestModeBanner() {
  const { testMode, user } = useAuth();

  if (!testMode?.active) return null;

  const handleSwitch = async (persona) => {
    if (persona === testMode.persona_role) return;
    try {
      const res = await api.enterTestMode(persona);
      toast.success(`Now viewing as ${PERSONA_META[persona].label} — ${res.data.persona.name}`);
      const target = PERSONA_META[persona]?.route || '/home';
      window.location.assign(target);
    } catch (e) {
      toast.error(e.response?.data?.detail || `No ${persona} account available`);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Wipe all your test-mode edits and re-sync this sandbox with current live data?')) return;
    try {
      await api.resetTestMode();
      toast.success('Sandbox reset — fresh copy of production loaded.');
      // Reload so every component re-fetches against the freshly-seeded sandbox.
      window.location.reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset sandbox');
    }
  };

  const handleExit = async () => {
    try {
      await api.exitTestMode();
      toast.success('Exited test mode');
      window.location.assign('/admin');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to exit test mode');
    }
  };

  const personaMeta = PERSONA_META[testMode.persona_role] || PERSONA_META.user;
  const PersonaIcon = personaMeta.icon;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-amber-500/95 via-orange-500/95 to-amber-500/95 text-black border-b-2 border-amber-700 shadow-lg backdrop-blur-sm"
      data-testid="test-mode-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <FlaskConical className="w-5 h-5 flex-shrink-0" />
          <span className="font-bold text-sm whitespace-nowrap">TEST MODE</span>
          <span className="hidden sm:inline text-sm opacity-80">— viewing as</span>
          <span className="text-sm font-semibold truncate flex items-center gap-1">
            <PersonaIcon className="w-3.5 h-3.5" />
            {personaMeta.label}: {user?.name || testMode.persona_name}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="bg-black/10 hover:bg-black/20 border-black/30 text-black text-xs font-semibold h-8"
                data-testid="test-mode-switch-btn"
              >
                Switch View
                <ChevronDown className="w-3.5 h-3.5 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-surface border-[#262626] text-white">
              <DropdownMenuLabel className="text-xs text-[#A1A1AA]">Switch persona</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-[#262626]" />
              {Object.entries(PERSONA_META).map(([key, meta]) => {
                const Icon = meta.icon;
                const active = testMode.persona_role === key;
                return (
                  <DropdownMenuItem
                    key={key}
                    onClick={() => handleSwitch(key)}
                    className={`cursor-pointer ${active ? 'text-lime' : ''}`}
                    data-testid={`test-mode-persona-${key}`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {meta.label}{active ? ' (current)' : ''}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            size="sm"
            variant="outline"
            onClick={handleReset}
            className="bg-black/10 hover:bg-black/20 border-black/30 text-black h-8 text-xs font-semibold"
            data-testid="test-mode-reset-btn"
            title="Wipe test edits and re-sync from live data"
          >
            <RotateCw className="w-3.5 h-3.5 mr-1" />
            Reset
          </Button>
          <Button
            size="sm"
            onClick={handleExit}
            className="bg-black hover:bg-black/80 text-amber-300 h-8 text-xs font-bold"
            data-testid="test-mode-exit-btn"
          >
            <X className="w-3.5 h-3.5 mr-1" />
            Exit
          </Button>
        </div>
      </div>
    </div>
  );
}
